import * as React from "react";
import { Drawer } from "@mui/material";
import { SlidersHorizontal, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export const CUSTOMIZABLE_SECTION_IDS = [
  "spend-per-project",
  "top-keys",
  "top-models",
  "top-deployments",
  "provider-usage",
] as const;

export type CustomizableSectionId = (typeof CUSTOMIZABLE_SECTION_IDS)[number];

export type UsageViewPreferences = {
  showPercentage: boolean;
  sections: Record<CustomizableSectionId, boolean>;
};

export const createDefaultUsageViewPreferences = (): UsageViewPreferences => ({
  showPercentage: true,
  sections: {
    "spend-per-project": true,
    "top-keys": true,
    "top-models": true,
    "top-deployments": true,
    "provider-usage": true,
  },
});

const SECTION_OPTIONS: {
  id: CustomizableSectionId;
  label: string;
}[] = [
  { id: "spend-per-project", label: "spendByProject" },
  { id: "top-keys", label: "topKeys" },
  { id: "top-models", label: "topModels" },
  { id: "top-deployments", label: "topGpuHours" },
  { id: "provider-usage", label: "providerUsage" },
];

type CustomizeViewDrawerProps = {
  preferences: UsageViewPreferences;
  onSave: (preferences: UsageViewPreferences) => void;
};

export function CustomizeViewDrawer({
  preferences,
  onSave,
}: CustomizeViewDrawerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(preferences);
  const isRTL = i18n.dir() === "rtl";

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft({ ...preferences, sections: { ...preferences.sections } });
    }
    setOpen(nextOpen);
  };

  const resetDraft = () => setDraft(createDefaultUsageViewPreferences());

  const saveDraft = () => {
    onSave(draft);
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        className="h-10 bg-input hover:bg-input/80"
        onClick={() => handleOpenChange(true)}
      >
        <SlidersHorizontal aria-hidden />
        {t("usageAndCost.customizeView.button")}
      </Button>
      <Drawer
        anchor={isRTL ? "left" : "right"}
        open={open}
        onClose={() => handleOpenChange(false)}
        PaperProps={{
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": "customize-view-title",
          sx: {
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            width: { xs: "100%", sm: 384 },
            backgroundColor: "var(--card)",
            backgroundImage: "none",
            borderLeft: isRTL ? undefined : "1px solid hsl(var(--border))",
            borderRight: isRTL ? "1px solid hsl(var(--border))" : undefined,
          },
        }}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 id="customize-view-title" className="text-base font-semibold">
              {t("usageAndCost.customizeView.title")}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={() => handleOpenChange(false)}
              aria-label={t("common.close")}
            >
              <X aria-hidden />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            <section>
              <h2 className="text-sm font-medium">
                {t("usageAndCost.customizeView.displayPreferences")}
              </h2>
              <div className="mt-3 flex items-center justify-between gap-4 border-b border-border py-3">
                <div className="min-w-0">
                  <p className="text-sm">
                    {t("usageAndCost.customizeView.showPercentage")}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("usageAndCost.customizeView.showPercentageDescription")}
                  </p>
                </div>
                <Switch
                  checked={draft.showPercentage}
                  onCheckedChange={(showPercentage) =>
                    setDraft((current) => ({ ...current, showPercentage }))
                  }
                  aria-label={t("usageAndCost.customizeView.showPercentage")}
                />
              </div>
            </section>

            <section className="mt-4">
              <h2 className="text-sm font-medium">
                {t("usageAndCost.customizeView.detailedSections")}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("usageAndCost.customizeView.detailedSectionsDescription")}
              </p>
              <div className="mt-3">
                {SECTION_OPTIONS.map(({ id, label }) => (
                  <div
                    key={id}
                    className="flex items-center justify-between gap-4 border-b border-border py-3"
                  >
                    <span className="text-sm">
                      {t(`usageAndCost.customizeView.${label}`)}
                    </span>
                    <Switch
                      checked={draft.sections[id]}
                      onCheckedChange={(checked) =>
                        setDraft((current) => ({
                          ...current,
                          sections: { ...current.sections, [id]: checked },
                        }))
                      }
                      aria-label={t(`usageAndCost.customizeView.${label}`)}
                    />
                  </div>
                ))}
              </div>
            </section>

            <p className="mt-4 text-xs text-muted-foreground">
              {t("usageAndCost.customizeView.summaryAlwaysVisible")}
            </p>
          </div>

          <div className="flex justify-between gap-2 border-t border-border px-5 py-4">
            <Button variant="outline" className="flex-1" onClick={resetDraft}>
              {t("usageAndCost.customizeView.resetToDefault")}
            </Button>
            <Button className="flex-1" onClick={saveDraft}>
              {t("usageAndCost.customizeView.save")}
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
}
